// ============================================================
// The roster write trace - who changed a combatant, from where
// ============================================================
// A sandbox-only peephole (window.__fuzzWrites, main.jsx). ./dev fuzz
// found screens re-writing one combatant at each other while nobody
// acted, and the write log could say THAT a screen wrote but not WHY:
// what changed the roster just before. Every roster writer notes the
// combatant, the kind of change and the call stack here; the fuzzer
// attaches the looping screens' entries to its finding.
//
// Off unless enabled (emulator builds only), so production never builds
// a stack per write. A ring: only the newest entries are kept.

import { canonicalJson } from '../engine/canonicalJson';

/** One change to the roster: which combatant ('*' for a whole-roster change), what kind, when and from where. */
export interface WriteTraceEntry {
  t: number;
  id: string;
  kind: string;
  stack: string;
  /** For an outgoing write: the fields that differ from what was last sent or received. */
  detail?: string;
}

const KEEP = 200;
let enabled = false;
let entries: WriteTraceEntry[] = [];

/** Switch the trace on (the sandbox) or off, starting empty. */
export function enableWriteTrace(on: boolean): void {
  enabled = on;
  entries = [];
}

/** Whether the trace is recording - so a caller can skip preparing a detail it would drop. */
export function isWriteTraceOn(): boolean {
  return enabled;
}

/** Note one roster change (or, with `detail`, an outgoing write). Free when the trace is off. */
export function noteRosterWrite(id: string, kind: string, detail?: string): void {
  if (!enabled) return;
  const entry = { t: Date.now(), id, kind, stack: new Error('roster write').stack ?? '' };
  entries.push(detail === undefined ? entry : { ...entry, detail });
  if (entries.length > KEEP) entries = entries.slice(entries.length - KEEP);
}

/** The newest entries, oldest first - a copy. */
export function readWriteTrace(): WriteTraceEntry[] {
  return entries.slice();
}

/**
 * The top-level fields of `now` whose values differ from `beforeJson` -
 * the echo cache's JSON of what was last sent or received - key order
 * aside. What an outgoing write is ABOUT, when a loop needs explaining.
 */
export function changedFields(beforeJson: string, now: unknown): string {
  if (!beforeJson) return '(first write)';
  const before = JSON.parse(beforeJson) as Record<string, unknown>;
  const after = (now ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  return keys.filter((k) => canonicalJson(before[k]) !== canonicalJson(after[k])).join(', ');
}
